import { Component, OnInit, OnDestroy, ComponentRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';

import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil, timeout } from 'rxjs/operators';
import { lastValueFrom } from 'rxjs';

import { Variables, VariableType } from '~/core/classes/user/variables';

import { AppSettingsService } from '~/core/services/appsettings.service';
import { AppConfigService } from '~/core/services/appconfig.service';
import { WorkspaceSwitcherService } from '~/core/services/workspace.service';

import { GraphService } from '~/core/services/graph.service';
import { HttpService } from '~/core/services/http.service';

import { AppSettings } from '~/core/models/appsettings';

let ALLOWED_EMAILS: string[] = [];

export interface Modal {
    type?: ComponentRef<any>;
    inputs?: any;
    outputs?: any
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    private appSettings: AppSettings = new AppSettings();

    public error: string = "";
    public title: string = "";
    public expand: boolean = false;

    public showNav: boolean = true;
    public showFooter: boolean = true;

    public layoutCssClass: string = "";

    public isLoggedIn: boolean = false;
    public isLoading: boolean = true;
    public isLoginPage: boolean = false;
    
    // Workspace tracking
    public showAdmin: boolean = false;
    public currentWorkspace: string = '';

    private readonly _destroying$ = new Subject<void>();

    // Alert
    public showAlert: boolean = false;
    public alertTitle: string = "Alert";
    public alertMessage: string = "";
    public alertContinueText: string = "OK";
    public alertCancelText: string = "";

    // Modal
    public modal: Modal | null = null;

    constructor(
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private graphService: GraphService,
        private router: Router,
        private appSettingsService: AppSettingsService,
        private appConfigService: AppConfigService,
        private workspaceSwitcherService: WorkspaceSwitcherService,
        private httpService: HttpService,
    ) {
        window.onbeforeunload = null;
        sessionStorage.removeItem("__allowNavigate__");
    }

    //*************************************************************************
    //  Component Life-Cycle Methods
    //*************************************************************************
    public async ngOnInit() {
        // Check if on login page
        this.checkIfLoginPage();

        // Subscribe to route changes
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._destroying$)
        ).subscribe((event: any) => {
            this.checkIfLoginPage();
        });

        // Subscribe to workspace changes to show/hide admin component
        this.workspaceSwitcherService.currentWorkspace$
            .pipe(takeUntil(this._destroying$))
            .subscribe(workspace => {
                this.currentWorkspace = workspace;
                // Show admin component if user is in admin-only workspace
                this.showAdmin = this.workspaceSwitcherService.isAdminOnlyWorkspace();
                console.log('[App] Workspace changed to:', workspace, 'Show Admin:', this.showAdmin);
            });

        try {
            const appSettings = await this.appSettingsService.getSettings(true);
            if (appSettings) {
                this.appSettings = appSettings;
                this.title = this.appSettings.title;
                this.showNav = this.appSettings.showNav;
                this.showFooter = this.appSettings.showFooter;

                // Get allowed emails from API
                // getUserIds returns an Observable, use lastValueFrom to convert to Promise
                try {
                    const result = await lastValueFrom(
                        this.httpService.getUserIds().pipe(timeout(10000))
                    );
                    if (result?.user_ids) {
                        ALLOWED_EMAILS = result.user_ids.map((user: any) => user.user_id?.toLowerCase());
                    }
                } catch (error) {
                    console.error('[App] ngOnInit - Error loading user IDs:', error);
                    // Continue even if user IDs fail to load
                }

                // Proceed to get MSAL accounts
                this.getMSALAccounts();
            } else {
                this.error = "Unable to load application settings.";
                this.isLoading = false;
            }
        } catch (error) {
            console.error('[App] ngOnInit - Error during initialization:', error);
            this.error = "Application initialization failed.";
            this.isLoading = false;
        }
    }

    //*************************************************************************
    //  Public Methods
    //*************************************************************************
    public setLayoutClass(change: boolean = true) {
        if (change) {
            if (this.layoutCssClass !== "") {
                this.layoutCssClass = "";
            } else {
                this.layoutCssClass = "expand";
            }
        }

        if (this.layoutCssClass === "") {
            this.expand = false;
        } else {
            setTimeout(() => {
                this.expand = true;
            }, 100);
        }

        Variables.set("Nav.LayoutCssClass", this.layoutCssClass, VariableType.Local);
    }

    public async reloadPage(page = "") {
        let currentUrl = page === "" ? this.router.url : page;

        this.router.navigate([currentUrl]);
    }

    //*************************************************************************
    //  Alert Methods
    //*************************************************************************
    public alertContinue() {
        this.showAlert = false;
    }

    public alertCancel() {
        this.showAlert = false;
    }

    //*************************************************************************
    //  Private Methods
    //*************************************************************************
    private checkIfLoginPage() {
        this.isLoginPage = this.router.url.includes('/auth');
    }

    private getMSALAccounts() {
        // Add timeout to prevent infinite waiting
        const timeoutId = setTimeout(() => {
            console.warn('[App] getMSALAccounts - Timeout waiting for auth to complete');
            this.isLoading = false;
        }, 15000); // 15 second timeout

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                clearTimeout(timeoutId);
                
                let account: AccountInfo | null = this.authService.instance.getActiveAccount();

                if (account === null) {
                    account = this.getAccountInfo();
                }

                this.setAccountInfo(account);
            });
    }

    private getAccountInfo() {
        let accounts = this.authService.instance.getAllAccounts();
        let account: AccountInfo | null = null;

        if (accounts.length > 0) {
            this.authService.instance.setActiveAccount(accounts[0]);
            account = accounts[0];
        }

        return account;
    }

    private async setAccountInfo(account: AccountInfo | null) {
        try {
            // If no account, user is not authenticated - let AuthGuard handle redirect to /auth
            if (!account) {
                console.log('[App] setAccountInfo - No account found, user not authenticated');
                this.isLoading = false;
                this.isLoggedIn = false;
                return;
            }

            let allowAccess: boolean = this.appSettings.openAccess;

            // Restrict access to only allowed emails loaded from API
            if (!this.appSettings.openAccess && account.username && !ALLOWED_EMAILS.includes(account.username.toLowerCase())) {
                allowAccess = false;
            }

            let roles: any = [];

            // Only process roles if roles array exists and is not empty
            if (this.appSettings.roles && Array.isArray(this.appSettings.roles) && this.appSettings.roles.length > 0) {
                for (let role of this.appSettings.roles) {
                    try {
                        let groupAccess: any = await this.graphService.getUserGroups(role);

                        if (groupAccess?.value && groupAccess.value.length > 0) {
                            roles.push(role);
                            console.log(roles, "roles");
                        }
                    } catch (error: any) {
                        console.error(`[App] setAccountInfo - Error fetching groups for role ${role}:`, error.message);
                        // Continue with next role even if current one fails
                    }
                }
            }

            if (!this.appSettings.openAccess && roles.length > 0) {
                allowAccess = true;
            }

            // NEW: Check membership in role-based groups using flat config structure
            const appConfig = this.appConfigService.getConfig();
            const roleGroupMapping = appConfig.getRoleGroupMapping();
            
            if (roleGroupMapping && Object.keys(roleGroupMapping).length > 0) {
                try {
                    console.log('[App] setAccountInfo - Checking user roles via role group mapping');
                    const userRoles = await this.graphService.getUserRoles(roleGroupMapping);
                    
                    if (userRoles.length > 0) {
                        console.log('[App] setAccountInfo - User has roles:', userRoles);
                        allowAccess = true;
                        
                        // Set up workspace switcher with user's available roles
                        this.workspaceSwitcherService.setAvailableRoles(userRoles);
                    } else {
                        console.log('[App] setAccountInfo - User is not member of any required roles, denying access');
                        allowAccess = false;
                    }
                } catch (error: any) {
                    console.error('[App] setAccountInfo - Error verifying user roles:', error.message);
                    // Default to deny access if we can't verify membership for security reasons
                    allowAccess = false;
                }
            } else {
                // FALLBACK: Use old single object_id check if roleGroupMapping not configured
                const requiredGroupObjectId = this.appConfigService.getConfig().object_id;
                if (requiredGroupObjectId) {
                    try {
                        console.log('[App] setAccountInfo - Verifying user membership in required AD group (fallback)');
                        const isMemberOfRequiredGroup = await this.graphService.isUserInRequiredGroup(requiredGroupObjectId);
                        
                        if (!isMemberOfRequiredGroup) {
                            console.log('[App] setAccountInfo - User is not member of required AD group, denying access');
                            allowAccess = false;
                        } else {
                            console.log('[App] setAccountInfo - User is member of required AD group, access allowed');
                            allowAccess = true;
                            
                            // Set up workspace switcher with single role as fallback
                            this.workspaceSwitcherService.setAvailableRoles([requiredGroupObjectId]);
                        }
                    } catch (error: any) {
                        console.error('[App] setAccountInfo - Error verifying AD group membership:', error.message);
                        // Default to deny access if we can't verify membership for security reasons
                        allowAccess = false;
                    }
                }
            }

            if (account) {
                await this.setUserInfo(account, roles);
                await this.appSettingsService.saveSettings(this.appSettings);
            }

            if (allowAccess) {
                this.layoutCssClass = "";
                this.isLoggedIn = true;
            } else {
                this.layoutCssClass = "noaccess";
                this.isLoggedIn = false;
            }
        } catch (error: any) {
            console.error('[App] setAccountInfo - Unexpected error:', error);
            this.layoutCssClass = "noaccess";
            this.isLoggedIn = false;
        } finally {
            this.isLoading = false;
        }
    }

    private async setUserInfo(account: any, roles: any) {
        account.photo = "";

        if (account.name) {
            const name: string[] = account.name.split(", ");

            if (name && name.length > 1) {
                const firstName: string = name[1].split(" (")[0].split(" [")[0];
                const lastName: string = name[0];

                account.firstName = firstName;
                account.lastName = lastName;
                account.name = `${firstName} ${lastName}`;
                account.initials = firstName.substring(0, 1).toUpperCase() + lastName.substring(0, 1).toUpperCase();
            }
        }

        account.roles = roles;

        this.appSettings.user = account;
    }

    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }
}
